import React, {Fragment, useEffect, useMemo, useState} from "react";
import {
    Trophy,
    Search,
    CheckCircle,
    XCircle,
    Clock3,
    CreditCard,
    Sparkles,
    Calendar,
    Users,
    User,
} from "lucide-react";

import {useGetTakeAwards} from "../api/award";
import RootHeader from "../../../layout/root/header";
import RenderIf from "../../../core/components/render-if";
import FileView from "../../../core/components/file-view";
import LoadMore from "../../../core/components/loadmore";

const theme = {
    primary: "#820f99",
    deep: "#301466",
    light: "#c084fc",
    accent: "#d946ef",
};

export default function MyAwardHistory() {

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    const {
        data: myAwardList,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        refetch,
        isPending,
    } = useGetTakeAwards({
        status: filter === "all" ? undefined : filter,
        search: debouncedSearch || undefined
    });

    const awards = useMemo(() => {
        return myAwardList?.pages?.flatMap(page => page.data ?? []) ?? [];
    }, [myAwardList]);

    const statusConfig = {
        take: {
            label: "دریافت شده",
            color: "#22c55e",
            bg: "#22c55e22",
            icon: <CheckCircle size={14}/>,
        },
        pay: {
            label: "پرداخت شده",
            color: "#f59e0b",
            bg: "#f59e0b22",
            icon: <CreditCard size={14}/>,
        },
        pending_take: {
            label: "در انتظار تایید",
            color: "#3b82f6",
            bg: "#3b82f622",
            icon: <Clock3 size={14}/>,
        },
        reject: {
            label: "رد شده",
            color: "#ef4444",
            bg: "#ef444422",
            icon: <XCircle size={14}/>,
        },
    };
    const stars = useMemo(() => {
        return [...Array(110)].map(() => ({
            width: Math.random() * 4 + 1,
            height: Math.random() * 4 + 1,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
        }));
    }, []);

    // const stats = useMemo(() => ({
    //     total: awards.length,
    //     take: awards.filter(i => i.status === "take").length,
    //     pay: awards.filter(i => i.status === "pay").length,
    //     pending_take: awards.filter(i => i.status === "pending_take").length,
    //     reject: awards.filter(i => i.status === "reject").length,
    // }), [awards]);


    return (
        <>
            <RootHeader
                title="جوایز من"
                haveHumberMenu
                navigateTo="/app/awards"
            />

            <div
                dir="rtl"
                className="min-h-screen pt-10 relative overflow-hidden"
                style={{
                    background: `linear-gradient(160deg,#0a0612 0%,${theme.deep} 40%,#0a0612 100%)`,
                }}
            >

                <div className="absolute inset-0 pointer-events-none">
                    {stars.map((star, i) => (
                        <div
                            key={i}
                            className="absolute rounded-full"
                            style={{
                                ...star,
                                background: theme.light,
                                opacity: .25,
                            }}
                        />
                    ))}
                </div>

                <div className="relative max-w-6xl mx-auto px-4 py-6">

                    <div
                        className="rounded-3xl p-5 mb-6"
                        style={{
                            background: "rgba(255,255,255,.05)",
                            backdropFilter: "blur(20px)",
                            border: `1px solid ${theme.light}22`,
                        }}
                    >
                        <div className="flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between">

                            <div>
                                <div className="flex items-center gap-3">
                                    <Trophy
                                        size={32}
                                        color={theme.accent}
                                        fill={theme.accent}
                                    />
                                    <h1 className="text-white text-3xl font-black">
                                        تاریخچه جوایز
                                    </h1>
                                </div>

                                <p
                                    className="mt-2 text-sm"
                                    style={{color: theme.light}}
                                >
                                    تمامی درخواست‌ها و جوایز ثبت شده شما
                                </p>
                            </div>

                            {/*<div className="flex flex-wrap gap-3">*/}
                            {/*    <StatCard title="کل" value={stats.total}/>*/}
                            {/*    <StatCard title="دریافت شده" value={stats.take}/>*/}
                            {/*    <StatCard title="در انتظار" value={stats.pending_take}/>*/}
                            {/*    <StatCard title="پرداخت شده" value={stats.pay}/>*/}
                            {/*    <StatCard title="رد شده" value={stats.reject}/>*/}
                            {/*</div>*/}

                        </div>
                    </div>

                    <div className="mb-4">
                        <div className="relative">
                            <Search
                                size={18}
                                color="#999"
                                className="absolute left-3 top-4"
                            />

                            <input
                                autoFocus
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="جستجوی جایزه..."
                                className="w-full rounded-2xl pr-10 px-4 py-3 text-white outline-none"
                                style={{
                                    background: "rgba(255,255,255,.05)",
                                    border: `1px solid ${theme.light}22`,
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">

                        <FilterButton
                            active={filter === "all"}
                            onClick={() => setFilter("all")}
                        >
                            همه
                        </FilterButton>

                        <FilterButton
                            active={filter === "take"}
                            onClick={() => setFilter("take")}
                        >
                            دریافت شده
                        </FilterButton>

                        <FilterButton
                            active={filter === "pay"}
                            onClick={() => setFilter("pay")}
                        >
                            پرداخت شده
                        </FilterButton>

                        <FilterButton
                            active={filter === "pending_take"}
                            onClick={() => setFilter("pending_take")}
                        >
                            در انتظار تایید
                        </FilterButton>

                        <FilterButton
                            active={filter === "reject"}
                            onClick={() => setFilter("reject")}
                        >
                            رد شده
                        </FilterButton>

                    </div>

                    <div className="space-y-4">
                        <RenderIf cond={isPending}>
                            <div className="text-white">
                                در حال دریافت اطلاعات...
                            </div>
                        </RenderIf>
                        {awards.map((item, i) => {

                            const award = item.award;

                            return (

                                <div
                                    key={`${item.id}-${i}`}
                                    className="rounded-3xl p-5"
                                    style={{
                                        background: "rgba(255,255,255,.05)",
                                        backdropFilter: "blur(16px)",
                                        border: `1px solid ${theme.light}22`,
                                    }}
                                >

                                    <div className="flex justify-between items-start gap-4">

                                        <div className="flex gap-4">

                                            <div
                                                className="w-14 h-14 rounded-2xl overflow-hidden flex items-center justify-center"
                                                style={{
                                                    background: `${theme.primary}55`,
                                                    border: `1px solid ${theme.accent}44`,
                                                }}
                                            >

                                                <RenderIf
                                                    cond={award?.fileDetail}
                                                    falsyElement={
                                                        <Sparkles
                                                            size={24}
                                                            color={theme.accent}
                                                        />
                                                    }
                                                >
                                                    <FileView
                                                        {...award?.fileDetail}
                                                        imageClassName="object-contain"
                                                    />
                                                </RenderIf>

                                            </div>

                                            <div>

                                                <div className="flex items-center gap-1">

                                                    <span className="text-[#c084fc] text-sm font-bold">
                                                        {award?.prefix?.title}
                                                    </span>

                                                    <h3 className="text-white font-bold text-lg">
                                                        {award?.title}
                                                    </h3>

                                                </div>

                                                <div
                                                    className="flex items-center gap-2 mt-1 text-sm"
                                                    style={{color: theme.light}}
                                                >

                                                    <RenderIf cond={award?.type === "user"}>
                                                        <>
                                                            <User size={14}/>
                                                            فردی
                                                        </>
                                                    </RenderIf>

                                                    <RenderIf cond={award?.type === "team"}>
                                                        <>
                                                            <Users size={14}/>
                                                            تیمی
                                                        </>
                                                    </RenderIf>

                                                </div>

                                                <RenderIf cond={award?.type === "team"}>
                                                    <div
                                                        className="flex items-center gap-2 mt-1 text-sm"
                                                        style={{color: theme.light}}
                                                    >
                                                        تیم {item?.team?.name}
                                                    </div>
                                                </RenderIf>

                                                <div
                                                    className="mt-2 text-sm"
                                                    style={{color: "#bbb"}}
                                                >

                                                    {award?.experience
                                                        ? `${award.experience} تجربه`
                                                        : award?.score
                                                            ? `${award.score} امتیاز`
                                                            : ""}

                                                </div>

                                            </div>

                                        </div>

                                        <div
                                            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
                                            style={{
                                                background: statusConfig[item.status]?.bg,
                                                color: statusConfig[item.status]?.color,
                                            }}
                                        >
                                            {statusConfig[item.status]?.icon}
                                            {statusConfig[item.status]?.label}
                                        </div>

                                    </div>

                                    <div
                                        className="flex items-center gap-2 mt-4 text-xs"
                                        style={{color: "#999"}}
                                    >
                                        <Calendar size={13}/>
                                        {new Date(item.createdAt).toLocaleDateString("fa-IR")}
                                    </div>

                                    {item.status === "reject" && item.description && (
                                        <div
                                            className="mt-4 rounded-2xl p-3"
                                            style={{
                                                background: "#ef444411",
                                                border: "1px solid #ef444433",
                                                color: "#fca5a5",
                                            }}
                                        >
                                            <div className="font-bold mb-1">
                                                دلیل رد:
                                            </div>

                                            {item.description}

                                        </div>
                                    )}

                                </div>

                            );

                        })}

                    </div>

                    {awards.length === 0 && (
                        <div
                            className="text-center py-20 rounded-3xl mt-6"
                            style={{
                                background: "rgba(255,255,255,.03)",
                                color: theme.light,
                            }}
                        >
                            هیچ موردی پیدا نشد
                        </div>
                    )}

                    <LoadMore
                        auto
                        fetchNextPage={fetchNextPage}
                        hasNextPage={hasNextPage === true}
                        isFetchingNextPage={isFetchingNextPage}
                    />

                </div>

            </div>
        </>
    );
}

function StatCard({title, value}) {
    return (
        <div
            className="px-5 py-3 rounded-2xl"
            style={{
                background: "rgba(255,255,255,.05)",
                border: "1px solid rgba(255,255,255,.08)",
            }}
        >
            <div
                className="text-xs"
                style={{color: "#bbb"}}
            >
                {title}
            </div>

            <div className="text-white text-xl font-black">
                {value}
            </div>
        </div>
    );
}

function FilterButton({children, active, onClick}) {
    return (
        <button
            onClick={onClick}
            className="px-4 py-2 rounded-2xl text-sm font-bold transition-all"
            style={{
                background: active
                    ? `linear-gradient(90deg, ${theme.accent}, ${theme.primary})`
                    : "rgba(255,255,255,.05)",
                color: "white",
                border: active
                    ? "none"
                    : `1px solid ${theme.light}22`,
            }}
        >
            {children}
        </button>
    );
}